/**
 * ApercuExercice — juger un exercice AVANT de le mettre sur la fiche.
 *
 * Le parcours mesuré avant ce composant : générer, ajouter à la fiche, exporter
 * le PDF, l'ouvrir, chercher l'exercice, défiler, et — s'il ne convient pas —
 * revenir, le supprimer, régénérer. Sept gestes et un aller-retour hors du
 * produit pour répondre à une question qui tient en un coup d'œil : « est-ce
 * que celui-là me va ? »
 *
 * L'aperçu répond à cette question là où elle se pose. Il rend l'exercice dans
 * la feuille A4 exacte du PDF — même largeur, même gabarit, même feuille de
 * style — parce qu'un aperçu qui ne montre pas ce qui sera imprimé ne dispense
 * pas d'imprimer.
 */
import React from "react";
import { Button } from "../ui/button";
import { Check, Plus, RefreshCw, X } from "lucide-react";
import SheetPreview from "./SheetPreview";

export default function ApercuExercice({
  exercice, ouvert, onFermer, onAjouter, onRemplacer, dansLaFiche, remplacementEnCours,
  titre,
}) {
  React.useEffect(() => {
    if (!ouvert) return undefined;
    const echap = (e) => { if (e.key === "Escape") onFermer(); };
    const precedent = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", echap);
    return () => {
      document.body.style.overflow = precedent;
      document.removeEventListener("keydown", echap);
    };
  }, [ouvert, onFermer]);

  if (!ouvert || !exercice) return null;

  const graine = exercice.metadata?.seed;
  const variante = titre || exercice.metadata?.blueprint_label || exercice.metadata?.blueprint_id_used;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Aperçu de l'exercice"
      onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}
    >
      <div className="w-full max-w-[860px] rounded-xl bg-card shadow-xl">
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-foreground">
              {variante || "Aperçu de l'exercice"}
            </div>
            <div className="text-xs text-muted-foreground">
              Tel qu'il sera imprimé — énoncé puis corrigé
              {graine ? ` · graine ${graine}` : ""}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {onRemplacer && (
              <Button variant="outline" size="sm" onClick={onRemplacer} disabled={remplacementEnCours}>
                <RefreshCw className={`mr-1.5 h-4 w-4 ${remplacementEnCours ? "animate-spin" : ""}`} />
                Une autre
              </Button>
            )}
            {onAjouter && (
              <Button size="sm" variant={dansLaFiche ? "secondary" : "default"} onClick={onAjouter}>
                {dansLaFiche
                  ? (<><Check className="mr-1.5 h-4 w-4" />Dans la fiche</>)
                  : (<><Plus className="mr-1.5 h-4 w-4" />Ajouter à la fiche</>)}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onFermer} aria-label="Fermer l'aperçu">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="max-h-[78vh] overflow-y-auto px-4 py-4">
          {/* Deux feuilles : l'énoncé, puis le corrigé. C'est exactement ce que
              produisent « PDF sujet » et « PDF corrigé ». */}
          <SheetPreview
            title="Aperçu"
            exercises={[{ ...exercice, uniqueId: exercice.id_exercice || "apercu" }]}
            vue="sujet"
            editable={false}
            ajusterAuContenu
          />
          <div className="mt-6 mb-2 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
            Corrigé
          </div>
          <SheetPreview
            title="Aperçu"
            exercises={[{ ...exercice, uniqueId: (exercice.id_exercice || "apercu") + "-c" }]}
            vue="corrige"
            editable={false}
            ajusterAuContenu
          />
        </div>
      </div>
    </div>
  );
}
